import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { heroSlides } from '../mock';

const VisitNowSection = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((p) => (p + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const goPrev = () =>
    setCurrent((p) => (p === 0 ? heroSlides.length - 1 : p - 1));
  const goNext = () => setCurrent((p) => (p + 1) % heroSlides.length);

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 bg-white relative overflow-hidden">
      <div className="max-w-[1280px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Showroom Slideshow */}
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-100 shadow-lg">
              {heroSlides.map((src, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    idx === current ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={src}
                    alt={`Showroom slide ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}

              {/* Controls */}
              <button
                onClick={goPrev}
                aria-label="Previous"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-black flex items-center justify-center transition-colors shadow-md"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goNext}
                aria-label="Next"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-black flex items-center justify-center transition-colors shadow-md"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Indicators */}
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {heroSlides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrent(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`transition-all rounded-full ${
                      idx === current ? 'w-7 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/60'
                    }`}
                  />
                ))}
              </div>

              {/* Eyebrow tag */}
              <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <span className="text-xs font-medium text-black tracking-wide">
                  Inside the outlet
                </span>
              </div>
            </div>
          </div>

          {/* Right: Visit Now Info */}
          <div>
            <p className="text-xs md:text-sm text-gray-500 mb-3 tracking-[0.3em] uppercase">
              Find Us
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-black mb-6 tracking-tight font-display-serif leading-[1.05]">
              Visit our outlet store
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-10">
              Explore our extensive selection of furniture at incredible Factory Outlet Prices. Free delivery within Sydney (conditions apply). Visit us at Satis Furniture, located at 12 Perry St, Campsie NSW 2194, Australia, and elevate your space with our stylish pieces.
            </p>

            <h3 className="text-lg font-bold text-black mb-6">
              For further details contact us on:
            </h3>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-black" strokeWidth={1.5} />
                </div>
                <div>
                  <a
                    href="tel:+61411418088"
                    className="text-base text-gray-700 hover:text-black"
                  >
                    (02) 97872567 / 0430890808 / 0411418088
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-black" strokeWidth={1.5} />
                </div>
                <a
                  href="mailto:wangjianjohn@hotmail.com"
                  className="text-base text-gray-700 hover:text-black"
                >
                  wangjianjohn@hotmail.com
                </a>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-black" strokeWidth={1.5} />
                </div>
                <p className="text-base text-gray-700">
                  12 Perry St, Campsie NSW 2194, Australia
                </p>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-black" strokeWidth={1.5} />
                </div>
                <p className="text-base text-gray-700">
                  Monday to Sunday: 10:30am to 5:30pm
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisitNowSection;
